import { useEffect, useState, useCallback } from 'react'
import { InvoiceRuleService, ErrorConstant } from 'utility'
import { CompanyInvoiceSettingService } from 'utility/services/company-invoice-setting'

export const useInvoiceSettings = () => {
    const [data, setData] = useState(null)
    const [rules, setRules] = useState([])
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [saveLoading, setSaveLoading] = useState(false)
    const [saveError, setSaveError] = useState('')
    const [deleteLoading, setDeleteLoading] = useState(false)
    const [deleteError, setDeleteError] = useState('')

    const fetchData = useCallback(async () => {
        try {
            setLoading(true)
            const result = await CompanyInvoiceSettingService.get()
            setData(result || null)
        } catch (e) {
            setError(ErrorConstant?.default || 'Something went wrong')
        } finally {
            setLoading(false)
        }
    }, [])

    // Fetch all invoice rules
    const fetchInvoiceRules = useCallback(async () => {
        try {
            setLoading(true)
            const result = await InvoiceRuleService.list()
            setRules(result || [])
            return { success: true, data: result }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch invoice rules'
            setError(errorMessage)
            return { success: false, error: errorMessage }
        } finally {
            setLoading(false)
        }
    }, [])

    // Get a single invoice rule
    const getInvoiceRule = useCallback(async (invoiceRuleId) => {
        try {
            setLoading(true)
            const result = await InvoiceRuleService.get(invoiceRuleId)
            console.log('API Response:', result);

            if (result) {
                // Map API response to form fields
                const mappedData = {
                    ...result,
                    // Map any fields that need transformation
                    ruleStatus: result.IsEnabled,
                    // Add other field mappings as needed
                }
                return { success: true, data: mappedData }
            }
            return { success: false, error: 'Invalid response from server' }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch invoice rule'
            setError(errorMessage)
            return { success: false, error: errorMessage }
        } finally {
            setLoading(false)
        }
    }, [])

    // Save or update an invoice rule
    const saveInvoiceRule = useCallback(async (ruleData) => {
        try {
            setSaveLoading(true)
            setSaveError('')
            const result = await InvoiceRuleService.save(ruleData)
            // Refresh the rules list after successful save
            await fetchInvoiceRules()
            return { success: true, data: result }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to save invoice rule'
            setSaveError(errorMessage)
            return { success: false, error: errorMessage }
        } finally {
            setSaveLoading(false)
        }
    }, [fetchInvoiceRules])

    // Delete an invoice rule
    const deleteInvoiceRule = useCallback(async (invoiceRuleId) => {
        try {
            setDeleteLoading(true)
            setDeleteError('')
            const result = await InvoiceRuleService.delete(invoiceRuleId)
            // Refresh the rules list after successful delete
            await fetchInvoiceRules()
            return { success: true, data: result }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to delete invoice rule'
            setDeleteError(errorMessage)
            return { success: false, error: errorMessage }
        } finally {
            setDeleteLoading(false)
        }
    }, [fetchInvoiceRules])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    // Load rules when component mounts
    useEffect(() => {
        fetchInvoiceRules()
    }, [fetchInvoiceRules])

    return {
        // General data and state
        data,
        rules,
        setData,
        error,
        loading,

        // Save operation
        saveLoading,
        saveError,
        saveInvoiceRule,

        // Delete operation
        deleteLoading,
        deleteError,
        deleteInvoiceRule,

        // Get operation
        getInvoiceRule,

        // Refresh functions
        refetch: fetchData,
        refetchRules: fetchInvoiceRules,
    }
}
